import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Topbar from '../layouts/Topbar';
import Sidebar from '../layouts/Sidebar';
import { useParams, useNavigate } from 'react-router-dom'
import { useLocation } from 'react-router-dom';
import { FaArrowLeft, FaEye, FaDownload, FaFilePdf } from 'react-icons/fa';
import { MdFlag } from 'react-icons/md';
import API_BASE_URL from '../../../config/api'

// Add a styled modal component
const Modal = ({ isOpen, onClose, children, style }) => {
  if (!isOpen) return null;
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
      background: 'rgba(30, 41, 59, 0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
    }}>
      <div style={{
        background: '#fff',
        padding: '32px 28px 24px 28px',
        borderRadius: 16,
        minWidth: 340,
        maxWidth: 420,
        boxShadow: '0 8px 32px rgba(30,41,59,0.18)',
        position: 'relative',
        fontFamily: 'inherit',
        animation: 'modalFadeIn 0.2s',
        ...style,
      }}>
        {/* Close icon */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute',
            top: 14,
            right: 14,
            background: 'none',
            border: 'none',
            fontSize: 22,
            color: '#64748b',
            cursor: 'pointer',
            fontWeight: 600,
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          &times;
        </button>
        {children}
      </div>
      <style>{`
        @keyframes modalFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

const AllEmployeeGoalsReview = () => {
  const baseUrl = API_BASE_URL
  const [goals, setGoals] = useState([]);
  const [groupedGoals, setGroupedGoals] = useState([]);
  const [flagging, setFlagging] = useState({}); // { [goalId]: true/false }
  const [flagFeedback, setFlagFeedback] = useState({}); // { [goalId]: 'success' | 'error' | '' }
  const { id } = useParams()
  const location = useLocation()
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const employeeId = location.state?.employeeId || id; // fallback to URL param
  const [employeeName, setEmployeeName] = useState('');
  const navigate = useNavigate();
  
  // Year filter state
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [filteredGoals, setFilteredGoals] = useState([]);

  // Function to filter goals by year
  const filterGoalsByYear = (goalsList, year) => {
    const filtered = goalsList.filter(goal => {
      // Use goal.year directly if it exists
      if (goal.year !== undefined && goal.year !== null) {
        return goal.year === year;
      }
      
      // Fallback to checking various date fields if goal.year doesn't exist
      const goalDate = goal.created_at || goal.updated_at || goal.start_date || goal.end_date || goal.date_created;
      if (!goalDate) {
        return true; // If no date, include the goal
      }
      
      const goalYear = new Date(goalDate).getFullYear();
      return goalYear === year;
    });
    
    return filtered;
  };

  // Add global CSS animation for blinking unreviewed goals
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes blinkUnreviewed {
        0% { background-color: #fef3c7 !important; }
        50% { background-color: #fbbf24 !important; }
        100% { background-color: #fef3c7 !important; }
      }
      .unreviewed-row {
        animation: blinkUnreviewed 1.5s infinite !important;
      }

      /* Responsive utilities matching HREmployeeGoalsReview design */
      .page-header .header-content { flex-wrap: wrap; gap: 12px; }
      .table-scroll { width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .goals-responsive-table { min-width: 780px; }
      @media (max-width: 768px) {
        .page-header .header-content { flex-direction: column; align-items: stretch; }
        .page-header .header-content > div { width: 100%; justify-content: space-between; }
        .table-scroll { margin: 0 -12px; padding: 0 12px; }
        .goals-responsive-table th, .goals-responsive-table td { font-size: 13px; padding: 10px 8px; }
        .final-total-row { justify-content: center !important; }
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [hodScore, setHodScore] = useState('');
  const [modalError, setModalError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // View modal state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewGoal, setViewGoal] = useState(null);

  // Flag modal state
  const [flagModalOpen, setFlagModalOpen] = useState(false);
  const [flagGoalId, setFlagGoalId] = useState(null);
  const [flagReason, setFlagReason] = useState('');
  const [flagModalError, setFlagModalError] = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);

  useEffect(() => {
    const fetchGoals = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = Cookies.get('token');
        const response = await axios.get(`${baseUrl}/api/employees/${employeeId}/`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        // Only include goals with status 'complete'
        const completedGoals = (Array.isArray(response?.data?.employee?.goals) ? response?.data?.employee?.goals : []).filter(goal => goal.status === 'complete' || goal.status === 'completed');
        setGoals(completedGoals);
        setEmployeeName(response?.data?.employee?.name || response?.data?.employee?.full_name || '');

        // Apply year filter to the fetched goals
        const yearFilteredGoals = filterGoalsByYear(completedGoals, selectedYear);
        setFilteredGoals(yearFilteredGoals);

        // Group filtered goals by KRA
        const kraMap = {};
        yearFilteredGoals.forEach(goal => {
          const kraName = goal.key_result_area.name || goal.kra_name || goal.key_result_area || 'Uncategorized';
          const kraPercentage = goal.key_result_area.percentage || 0;
          if (!kraMap[kraName]) kraMap[kraName] = { goals: [], percentage: kraPercentage };
          kraMap[kraName].goals.push(goal);
        });
        const grouped = Object.entries(kraMap).map(([kra, value]) => ({
          kra,
          goals: value.goals,
          percentage: value.percentage
        }));
        setGroupedGoals(grouped);
      } catch (err) {
        setError('Failed to fetch goals.');
      } finally {
        setLoading(false);
      }
    };
    fetchGoals();
  }, [employeeId, selectedYear]);

  // Update filtered goals when year selection changes
  useEffect(() => {
    if (goals.length > 0) {
      const yearFilteredGoals = filterGoalsByYear(goals, selectedYear);
      setFilteredGoals(yearFilteredGoals);
      
      // Regroup filtered goals by KRA
      const kraMap = {};
      yearFilteredGoals.forEach(goal => {
        const kraName = goal.key_result_area.name || goal.kra_name || goal.key_result_area || 'Uncategorized';
        const kraPercentage = goal.key_result_area.percentage || 0;
        if (!kraMap[kraName]) kraMap[kraName] = { goals: [], percentage: kraPercentage };
        kraMap[kraName].goals.push(goal);
      });
      const grouped = Object.entries(kraMap).map(([kra, value]) => ({
        kra,
        goals: value.goals,
        percentage: value.percentage
      }));
      setGroupedGoals(grouped);
    }
  }, [selectedYear, goals]);

  // Handle Review button click
  const handleReviewClick = (goal) => {
    setSelectedGoal(goal);
    setHodScore('');
    setModalError('');
    setModalOpen(true);
  };

  // Handle View button click
  const handleViewClick = (goal) => {
    setViewGoal(goal);
    setViewModalOpen(true);
  };

  // Generate report data
  const generateReportData = () => {
    const kraMap = {};
    filteredGoals.forEach(g => {
      const kraName = (g.key_result_area && (g.key_result_area.name || g.key_result_area)) || g.kra_name || 'Uncategorized';
      if (!kraMap[kraName]) kraMap[kraName] = [];
      kraMap[kraName].push(g);
    });

    const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
    const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
    const percentage = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';

    return {
      employeeName,
      year: selectedYear,
      kras: Object.entries(kraMap),
      totalHod,
      totalMetric,
      percentage,
      goals: filteredGoals
    };
  };

  // Generate Professional PDF Report
  const generatePDF = async () => {
    try {
      const reportData = generateReportData();
      
      // Dynamically import jsPDF
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF('p', 'mm', 'a4');
      
      // Colors
      const primaryColor = [59, 130, 246]; // Blue
      const darkGray = [55, 65, 81]; // Dark gray
      const lightGray = [156, 163, 175]; // Light gray
      const redColor = [220, 38, 38]; // Red for flagged
      
      // Helper function to wrap text
      const wrapText = (text, maxWidth) => {
        const words = text.split(' ');
        const lines = [];
        let currentLine = '';
        
        for (let word of words) {
          const testLine = currentLine + (currentLine ? ' ' : '') + word;
          const testWidth = doc.getTextWidth(testLine);
          
          if (testWidth > maxWidth && currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            currentLine = testLine;
          }
        }
        
        if (currentLine) {
          lines.push(currentLine);
        }
        
        return lines;
      };
      
      // Helper function to draw table
      const drawTable = (headers, rows, startY, maxWidth = 180) => {
        const colWidths = [60, 40, 20, 20, 20, 20]; // Adjust based on content
        const rowHeight = 8;
        const headerHeight = 10;
        
        // Draw header
        doc.setFillColor(...primaryColor);
        doc.rect(15, startY, maxWidth, headerHeight, 'F');
        
        let x = 15;
        headers.forEach((header, i) => {
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(10);
          doc.setFont(undefined, 'bold');
          doc.text(header, x + 2, startY + 6);
          x += colWidths[i];
        });
        
        // Draw rows
        let currentY = startY + headerHeight;
        doc.setTextColor(0, 0, 0);
        doc.setFont(undefined, 'normal');
        doc.setFontSize(9);
        
        rows.forEach((row, rowIndex) => {
          // Alternate row colors
          if (rowIndex % 2 === 0) {
            doc.setFillColor(249, 250, 251);
            doc.rect(15, currentY, maxWidth, rowHeight, 'F');
          }
          
          // Draw row content
          x = 15;
          let maxLines = 1;
          row.forEach((cell, colIndex) => {
            const cellText = String(cell || '');
            const wrappedText = wrapText(cellText, colWidths[colIndex] - 4);
            maxLines = Math.max(maxLines, wrappedText.length);
            
            wrappedText.forEach((line, lineIndex) => {
              doc.text(line, x + 2, currentY + 5 + (lineIndex * 3.5));
            });
            
            x += colWidths[colIndex];
          });
          
          currentY += Math.max(rowHeight, maxLines * 3.5 + 2);
        });
        
        return currentY;
      };
      
      let yPosition = 20;
      
      // Header
      doc.setTextColor(...primaryColor);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Report', 105, yPosition, { align: 'center' });
      
      yPosition += 15;
      doc.setTextColor(...darkGray);
      doc.setFontSize(16);
      doc.setFont(undefined, 'normal');
      doc.text(`for ${employeeName} - Year: ${selectedYear}`, 105, yPosition, { align: 'center' });
      
      
      yPosition += 8;
      
      
      // Performance Summary Box
      doc.setFillColor(248, 250, 252);
      doc.rect(15, yPosition, 180, 25, 'F');
      doc.setDrawColor(...primaryColor);
      doc.rect(15, yPosition, 180, 35);
      
      yPosition += 8;
      doc.setTextColor(...darkGray);
      doc.setFontSize(14);
      doc.setFont(undefined, 'bold');
      doc.text('Performance Summary', 20, yPosition);
      
      yPosition += 8;
      doc.setFontSize(11);
      doc.setFont(undefined, 'normal');
      doc.text(`Total Final Score: ${reportData.totalHod} / ${reportData.totalMetric}`, 20, yPosition);
      
      yPosition += 5;
      doc.text(`Performance Percentage: ${reportData.percentage}%`, 20, yPosition);
      
      yPosition += 5;
      doc.text(`Number of Goals: ${reportData.goals.length}`, 20, yPosition);
      
      yPosition += 20;
      
      // KRA Sections
      reportData.kras.forEach(([kraName, kraGoals]) => {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }
        
        // KRA Header
        doc.setFillColor(...primaryColor);
        doc.rect(15, yPosition, 180, 12, 'F');
        doc.setTextColor(255, 255, 255);
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`KRA: ${kraName}`, 20, yPosition + 8);
        
        yPosition += 15;
        
        // Prepare table data
        const headers = ['Activity/KPI', 'Standard', 'Metric', 'Final Score'];
        const rows = kraGoals.map(goal => [
          goal.activity || 'N/A',
          goal.performance_standard || 'N/A',
          goal.metric || 'N/A',
          goal.hod_score !== undefined ? goal.hod_score : 'Not Reviewed',
          
        ]);
        
        // Add KRA total row
        const kraTotal = kraGoals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0);
        const kraMetric = kraGoals.reduce((s, g) => s + (Number(g.metric) || 0), 0);
        rows.push(['KRA Total', '', '', `${kraTotal}/${kraMetric}`, '', '']);
        
        // Draw table
        yPosition = drawTable(headers, rows, yPosition);
        yPosition += 15;
      });
      
      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setTextColor(...lightGray);
        doc.setFontSize(8);
        doc.text('Generated by Performance Management System', 15, 290);
        doc.text(`Page ${i} of ${pageCount}`, 175, 290, { align: 'right' });
      }
      
      // Save the PDF
      const fileName = `Performance Report for ${employeeName} for ${selectedYear}.pdf`;
      doc.save(fileName);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF report. Please try again.');
    }
  };

  // Handle modal submit
  const handleModalSubmit = async (e) => {
    e.preventDefault();
    if (!selectedGoal) return;
    const maxScore = selectedGoal.metric;
    const parsedScore = Number(hodScore);
    if (isNaN(parsedScore) || parsedScore < 0) {
      setModalError('Please enter a valid score.');
      return;
    }
    if (parsedScore > maxScore) {
      setModalError(`HOD score cannot be greater than the goal metric (${maxScore}).`);
      return;
    }
    setSubmitting(true);
    setModalError('');
    try {
      const token = Cookies.get('token');
      await axios.post(
        `${baseUrl}/api/goals/${selectedGoal.id}/score/`,
        { hod_score: parsedScore },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      // Update the goal in the list
      setGoals(goals => goals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g));
      setFilteredGoals(filteredGoals => filteredGoals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g));
      
      // Update grouped goals as well
      setGroupedGoals(groupedGoals => groupedGoals.map(group => ({
        ...group,
        goals: group.goals.map(g => g.id === selectedGoal.id ? { ...g, hod_score: parsedScore } : g)
      })));
      
      setModalOpen(false);
      setSelectedGoal(null);
      setHodScore('');
    } catch (err) {
      setModalError('Failed to submit HOD score.');
    } finally {
      setSubmitting(false);
    }
  };

  // Open flag modal
  const handleFlagUnfair = (goalId) => {
    setFlagGoalId(goalId);
    setFlagReason('');
    setFlagModalError('');
    setFlagModalOpen(true);
  };

  // Submit flag with reason
  const handleFlagSubmit = async (e) => {
    e.preventDefault();
    if (!flagGoalId) return;
    if (!flagReason || flagReason.trim().length < 3) {
      setFlagModalError('Please provide a brief reason (min 3 characters).');
      return;
    }
    setFlagSubmitting(true);
    setFlagging(f => ({ ...f, [flagGoalId]: true }));
    setFlagFeedback(f => ({ ...f, [flagGoalId]: '' }));
    try {
      const token = Cookies.get('token');
      await axios.post(`${baseUrl}/api/goals/${flagGoalId}/flag/`, { flagging_reason: flagReason.trim() }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      setFlagFeedback(f => ({ ...f, [flagGoalId]: 'success' }));
      // mark goal as flagged in local state
      setGoals(goals => goals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g));
      setFilteredGoals(filteredGoals => filteredGoals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g));
      
      // Update grouped goals as well
      setGroupedGoals(groupedGoals => groupedGoals.map(group => ({
        ...group,
        goals: group.goals.map(g => g.id === flagGoalId ? { ...g, flagged: true } : g)
      })));
      
      setFlagModalOpen(false);
      setFlagGoalId(null);
      setFlagReason('');
    } catch (err) {
      setFlagFeedback(f => ({ ...f, [flagGoalId]: 'error' }));
      setFlagModalError('Failed to flag this goal. Please try again.');
    } finally {
      setFlagSubmitting(false);
      setFlagging(f => ({ ...f, [flagGoalId]: false }));
    }
  };

  return (
    <>
      <Topbar />
      <Sidebar />
      <div className="employee-goals-container">
        <div className="page-header">
          <div className="header-content" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
              <button
                onClick={() => navigate(-1)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  background: '#f1f5f9', color: '#334155', border: 'none', borderRadius: 6,
                  padding: '7px 16px', fontWeight: 500, fontSize: 15, cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.04)', transition: 'background 0.18s',
                  marginRight: 8
                }}
              >
                <FaArrowLeft /> Back
              </button>
              <div className="page-title">
                <h1 style={{ margin: 0, fontSize: '20px' }}>Employee Goals Review</h1>
                <p style={{ margin: 0, fontSize: '12px' }}>Review completed employee goals and assign HOD scores</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontSize: 15, fontWeight: 500, color: '#fff' }}>Year:</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: 14,
                    background: '#fff',
                    color: '#374151',
                    cursor: 'pointer'
                  }}
                >
                  {Array.from({ length: 2 }, (_, i) => {
                    const year = new Date().getFullYear() - 2 + i;
                    return (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    );
                  })}
                </select>
              </div>
              
              {/* Download PDF Report Button */}
              {filteredGoals.length > 0 && (
                <button
                  onClick={generatePDF}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    background: '#dc2626',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    padding: '10px 12px',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.2)',
                    transition: 'all 0.3s ease',
                    width: 'auto',
                    minWidth: 'auto',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap'
                  }}
                  onMouseOver={e => {
                    e.currentTarget.style.background = '#b91c1c';
                  }}
                  onMouseOut={e => {
                    e.currentTarget.style.background = '#dc2626';
                    e.currentTarget.style.padding = '10px 12px';
                    e.currentTarget.style.minWidth = 'auto';
                  }}
                  title="Download PDF Report"
                >
                  <FaFilePdf style={{ fontSize: 16, marginRight: 4 }} />
                  <FaDownload style={{ fontSize: 14 }} />
                </button>
              )}
              
            {employeeName && (
              <div style={{ fontWeight: 600, fontSize: 15, color: 'navy', background: '#f1f5f9', padding: '8px 18px', borderRadius: 8, boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
                {employeeName}
              </div>
            )}
            </div>
          </div>
        </div>
        <div className="hr-content-wrapper">
          {loading ? (
            <div className="loading-state">Loading goals...</div>
          ) : error ? (
            <div className="error-state">{error}</div>
          ) : (
            groupedGoals.length === 0 ? (
              <div className="empty-row">No completed goals found for {selectedYear}.</div>
            ) : (
              <>
                {groupedGoals.map((group, idx) => (
                <div key={group.kra || idx} style={{ marginBottom: 36, background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(30,41,59,0.06)', padding: '24px 20px' }}>
                  <div className="table-scroll">
                  <table className="goals-table goals-responsive-table" style={{ width: '100%', borderCollapse: 'collapse', background: '#f8fafc', borderRadius: 8, overflow: 'hidden' }}>
                    <thead>
                      <tr style={{ background: '#fffb' }}>
                        <th colSpan={5} style={{ padding: '10px 8px', textAlign: 'left', fontWeight: 600, color: '#334155', width: '100%' }}>
                            KRA: {group.kra} &gt; Weight: {group.percentage}%
                        </th>
                      </tr>
                      <tr style={{ background: '#e0e7ef' }}>
                        <th>Activity / Key Performance Indicator</th>
                        <th>Performance Standard / Target</th>
                        <th>Self Score</th>
                        <th>HOD Score</th>
                        <th>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                        {group.goals.map((goal, gidx) => {
                          const isUnreviewed = (goal.hod_score === 0 || goal.hod_score === undefined);
                          if (isUnreviewed) {
                            console.log('Unreviewed goal found:', goal.activity, 'hod_score:', goal.hod_score);
                          }
                          return (
                            <tr 
                              key={goal.id || gidx}
                              className={isUnreviewed ? 'unreviewed-row' : ''}
                            >
                          <td>{goal.activity || '[No Activity]'}</td>
                          <td>{goal.performance_standard !== undefined ? goal.performance_standard : '-'}</td>
                          <td>{goal.score !== undefined ? goal.score : '-'}</td>
                          <td>{goal.hod_score !== undefined ? goal.hod_score : '-'}</td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}>
                              {goal.flagged ? (
                                <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                  <MdFlag style={{ color: '#ef4444', fontSize: 20 }} />
                                  <span style={{ color: '#ef4444', fontWeight: 600, fontSize: 13 }}>Flagged</span>
                                </span>
                              ) : (
                                <>
                                  <button
                                    className='flag-button action-button'
                                    title='Flag Unfair HOD Score'
                                    style={{ color: '#eab308', background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, padding: 0, marginRight: 8, display: 'flex', alignItems: 'center' }}
                                    disabled={flagging[goal.id]}
                                    onClick={() => handleFlagUnfair(goal.id)}
                                  >
                                    <MdFlag />
                                  </button>
                                  {flagFeedback[goal.id] === 'success' && (
                                    <span style={{ color: '#22c55e', fontSize: 13, marginLeft: 4 }}>Flagged</span>
                                  )}
                                  {flagFeedback[goal.id] === 'error' && (
                                    <span style={{ color: '#ef4444', fontSize: 13, marginLeft: 4 }}>Error</span>
                                  )}
                                </>
                              )}
                              <button className='view-button action-button' onClick={() => handleViewClick(goal)}>
                                <FaEye />
                              </button>
                            </div>
                          </td>
                        </tr>
                          );
                        })}
                        {/* KRA subtotal row */}
                        <tr>
                          <td colSpan={3} style={{ textAlign: 'right', fontWeight: 700, background: '#f8fafc' }}>KRA Final</td>
                          <td style={{ fontWeight: 800, color: '#0f172a', background: '#f8fafc' }}>
                            {group.goals.reduce((s, g) => s + (Number(g.hod_score) || 0), 0)} / {group.goals.reduce((s, g) => s + (Number(g.metric) || 0), 0)}
                          </td>
                          <td style={{ background: '#f8fafc' }}></td>
                        </tr>
                    </tbody>
                  </table>
                </div>
                </div>
                ))}
                
                {/* Final Total Summary */}
                {filteredGoals.length > 0 && (() => {
                  const totalHod = filteredGoals.reduce((sum, g) => sum + (Number(g.hod_score) || 0), 0);
                  const totalMetric = filteredGoals.reduce((sum, g) => sum + (Number(g.metric) || 0), 0);
                  const pct = totalMetric > 0 ? ((totalHod / totalMetric) * 100).toFixed(2) : '0.00';
                  return (
                    <div className="final-total-row" style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                      <div style={{
                        display: 'flex', alignItems: 'center', gap: 14,
                        background: '#ffffff',
                        border: '1px solid #e2e8f0',
                        borderRadius: 12,
                        padding: '10px 16px',
                        boxShadow: '0 2px 8px rgba(30,41,59,0.06)'
                      }}>
                        <div style={{ fontWeight: 700, color: '#0f172a' }}>Final Total</div>
                        <div style={{ fontWeight: 800, color: '#0f172a' }}>{totalHod} / {totalMetric}</div>
                        <div style={{
                          marginLeft: 8,
                          background: '#e0f2fe',
                          color: '#0369a1',
                          fontWeight: 800,
                          padding: '4px 10px',
                          borderRadius: 9999,
                          border: '1px solid #bae6fd'
                        }}>{pct}%</div>
                      </div>
                    </div>
                  );
                })()}
              </>
            )
          )}
        </div>
      </div>
      {/* Modal for HOD score input */}
      <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Review Goal</h2>
        {selectedGoal && (
          <form onSubmit={handleModalSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div>
              <div style={{ fontSize: 15, color: '#334155', marginBottom: 2 }}>Activity</div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}>{selectedGoal.activity}</div>
            </div>
            <div>
              <div style={{ fontSize: 15, color: '#334155', marginBottom: 2 }}>Goal Metric ({selectedGoal.metric})</div>
              <div style={{ fontWeight: 500, color: '#0f172a' }}></div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <label htmlFor="hod-score-input" style={{ fontSize: 15, color: '#334155' }}>HOD Score</label>
              <input
                id="hod-score-input"
                type="number"
                min="0"
                max={selectedGoal.metric}
                value={hodScore}
                onChange={e => setHodScore(e.target.value)}
                style={{
                  padding: '8px 12px',
                  border: '1px solid #cbd5e1',
                  borderRadius: 6,
                  fontSize: 16,
                  width: 120,
                  outline: 'none',
                  transition: 'border 0.2s',
                  boxSizing: 'border-box',
                }}
                required
                disabled={submitting}
              />
            </div>
            {modalError && <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}>{modalError}</div>}
            <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
              <button
                type="submit"
                disabled={submitting}
                style={{
                  background: submitting ? '#a7f3d0' : '#22c55e',
                  color: '#fff',
                  padding: '8px 22px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 600,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  boxShadow: '0 1px 2px rgba(16,185,129,0.08)',
                  transition: 'background 0.2s',
                }}
              >
                {submitting ? 'Submitting...' : 'Submit'}
              </button>
              <button
                type="button"
                onClick={() => setModalOpen(false)}
                disabled={submitting}
                style={{
                  background: '#f1f5f9',
                  color: '#334155',
                  padding: '8px 18px',
                  border: 'none',
                  borderRadius: 6,
                  fontWeight: 500,
                  fontSize: 16,
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  borderBottom: '2px solid #e2e8f0',
                  transition: 'background 0.2s',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
      {/* Modal for flagging unfair HOD score */}
      <Modal isOpen={flagModalOpen} onClose={() => setFlagModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Flag Unfair HOD Score</h2>
        <form onSubmit={handleFlagSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ fontSize: 15, color: '#334155' }}>Please provide a brief reason:</div>
          <textarea
            value={flagReason}
            onChange={e => setFlagReason(e.target.value)}
            rows={4}
            style={{
              padding: '10px 12px',
              border: '1px solid #cbd5e1',
              borderRadius: 8,
              fontSize: 15,
              resize: 'vertical',
              outline: 'none'
            }}
            placeholder="Describe why the HOD score is unfair"
            disabled={flagSubmitting}
          />
          {flagModalError && <div style={{ color: '#ef4444', background: '#fef2f2', borderRadius: 4, padding: '6px 10px', fontSize: 14 }}>{flagModalError}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            <button
              type="submit"
              disabled={flagSubmitting}
              style={{
                background: flagSubmitting ? '#fde68a' : '#f59e0b',
                color: '#fff',
                padding: '8px 22px',
                border: 'none',
                borderRadius: 6,
                fontWeight: 600,
                fontSize: 16,
                cursor: flagSubmitting ? 'not-allowed' : 'pointer',
                boxShadow: '0 1px 2px rgba(245,158,11,0.18)',
                transition: 'background 0.2s',
              }}
            >
              {flagSubmitting ? 'Submitting...' : 'Submit'}
            </button>
            <button
              type="button"
              onClick={() => setFlagModalOpen(false)}
              disabled={flagSubmitting}
              style={{
                background: '#f1f5f9',
                color: '#334155',
                padding: '8px 18px',
                border: 'none',
                borderRadius: 6,
                fontWeight: 500,
                fontSize: 16,
                cursor: flagSubmitting ? 'not-allowed' : 'pointer',
                borderBottom: '2px solid #e2e8f0',
                transition: 'background 0.2s',
              }}
            >
              Cancel
            </button>
          </div>
        </form>
      </Modal>
      {/* Modal for viewing goal details */}
      <Modal style={{ minWidth: 640 }} isOpen={viewModalOpen} onClose={() => setViewModalOpen(false)}>
        <h2 style={{ margin: 0, marginBottom: 18, fontWeight: 700, fontSize: 22, color: '#0f172a', letterSpacing: 0.2 }}>Goal Details</h2>
        {viewGoal && (
          <>
            {viewGoal.flagged && viewGoal.flagging_reason ? (
              <div style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 10,
                background: '#fef2f2',
                color: '#991b1b',
                border: '1px solid #fecaca',
                borderLeft: '5px solid #ef4444',
                borderRadius: 10,
                padding: '12px 14px',
                marginBottom: 14,
                boxShadow: '0 1px 2px rgba(239,68,68,0.08)'
              }}>
                <MdFlag style={{ color: '#ef4444', fontSize: 22, flexShrink: 0, marginTop: 2 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: '#b91c1c', letterSpacing: 0.2 }}>Flag Reason</div>
                  <div style={{ whiteSpace: 'pre-wrap', fontSize: 15, color: '#7f1d1d' }}>{viewGoal.flagging_reason}</div>
                </div>
              </div>
            ) : null}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0, minWidth: 640, background: '#f8fafc', borderRadius: 12, boxShadow: '0 1px 4px rgba(30,41,59,0.06)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>
            {[
              { label: 'Activity / KPI', value: viewGoal.activity },
              { label: 'Performance Standard / Target', value: viewGoal.performance_standard },
              { label: 'Metric', value: viewGoal.metric || viewGoal.score },
              { label: 'Self Score', value: viewGoal.score },
              { label: 'HOD Score', value: viewGoal.hod_score !== undefined ? viewGoal.hod_score : '-' },
            ].map((row, idx, arr) => (
              <div
                key={row.label}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '14px 20px',
                  background: idx % 2 === 0 ? '#fff' : '#f1f5f9',
                  borderBottom: idx !== arr.length - 1 ? '1px solid #e2e8f0' : 'none',
                  fontSize: 16,
                  transition: 'background 0.18s',
                }}
                onMouseOver={e => (e.currentTarget.style.background = '#e0e7ef')}
                onMouseOut={e => (e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#f1f5f9')}
              >
                <span style={{ color: '#334155', fontWeight: 500 }}>{row.label}</span>
                  <span style={{ color: '#0f172a', fontWeight: 600, whiteSpace: 'pre-wrap' }}>{row.value}</span>
              </div>
            ))}
          </div>
          </>
        )}
      </Modal>
    </>
  );
};

export default AllEmployeeGoalsReview;
